import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, ChevronDown, ChevronRight, ArrowDown } from 'lucide-react';
import type { LogEntry, LogLevel, LogCategory } from '../types';

interface LogContentProps {
  entries: LogEntry[];
  onClear: () => void;
}

const LEVEL_FILTERS: { value: LogLevel | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'error', label: 'Error' },
  { value: 'warn', label: 'Warn' },
  { value: 'info', label: 'Info' },
];

const CATEGORY_COLORS: Record<LogCategory, string> = {
  action: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  expression: 'bg-purple-500/15 text-purple-700 dark:text-purple-400',
  state: 'bg-green-500/15 text-green-700 dark:text-green-400',
  event: 'bg-orange-500/15 text-orange-700 dark:text-orange-400',
  navigation: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-400',
  render: 'bg-gray-500/15 text-gray-700 dark:text-gray-400',
  general: 'bg-muted text-muted-foreground',
};

const LEVEL_INDICATOR_COLORS: Record<LogLevel, string> = {
  log: 'bg-muted-foreground',
  info: 'bg-blue-500',
  warn: 'bg-yellow-500',
  error: 'bg-red-500',
};

const LEVEL_ROW_COLORS: Record<LogLevel, string> = {
  log: '',
  info: '',
  warn: 'bg-yellow-500/5',
  error: 'bg-red-500/5',
};

function formatTimestamp(timestamp: number): string {
  const d = new Date(timestamp);
  return d.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  } as Intl.DateTimeFormatOptions);
}

function formatData(data: unknown): string {
  if (data === undefined || data === null) return '';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

function LogEntryRow({ entry }: { entry: LogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasData = entry.data !== undefined && entry.data !== null;

  return (
    <div
      className={cn('border-b border-border/50 px-3 py-1.5 text-xs', LEVEL_ROW_COLORS[entry.level])}
    >
      <div className="flex items-start gap-2">
        {/* Level indicator dot */}
        <span
          className={cn(
            'mt-1.5 h-2 w-2 shrink-0 rounded-full',
            LEVEL_INDICATOR_COLORS[entry.level],
          )}
        />

        {/* Timestamp */}
        <span className="shrink-0 font-mono text-muted-foreground">
          {formatTimestamp(entry.timestamp)}
        </span>

        {/* Category badge */}
        <Badge
          variant="secondary"
          className={cn(
            'shrink-0 px-1.5 py-0 text-[10px] font-medium leading-4 rounded',
            CATEGORY_COLORS[entry.category],
          )}
        >
          {entry.category}
        </Badge>

        {/* Message */}
        <span className="min-w-0 flex-1 font-mono break-all">{entry.message}</span>

        {/* Expand toggle */}
        {hasData && (
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4 shrink-0"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </Button>
        )}
      </div>

      {/* Expanded data */}
      {expanded && hasData && (
        <pre className="mt-1 ml-4 overflow-x-auto rounded bg-muted/50 p-2 font-mono text-[11px] text-muted-foreground">
          {formatData(entry.data)}
        </pre>
      )}
    </div>
  );
}

const AUTO_SCROLL_THRESHOLD = 50;

export function LogContent({ entries, onClear }: LogContentProps) {
  const [levelFilter, setLevelFilter] = useState<LogLevel | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<LogCategory | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const filteredEntries = entries.filter((entry) => {
    if (levelFilter !== 'all' && entry.level !== levelFilter) return false;
    if (categoryFilter && entry.category !== categoryFilter) return false;
    return true;
  });

  // Track scroll position
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAtBottom(distanceFromBottom < AUTO_SCROLL_THRESHOLD);
  }, []);

  // Auto-scroll when new entries arrive and user is near bottom
  useEffect(() => {
    if (isAtBottom && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [filteredEntries.length, isAtBottom]);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      setIsAtBottom(true);
    }
  }, []);

  // Collect present categories for filter pills
  const presentCategories = Array.from(new Set(entries.map((e) => e.category)));

  if (entries.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground">
          No logs yet. Interact with the preview to see logs here.
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b px-3 py-1.5">
        {/* Level filter */}
        <div className="flex items-center gap-1">
          {LEVEL_FILTERS.map((f) => (
            <Button
              key={f.value}
              variant={levelFilter === f.value ? 'default' : 'ghost'}
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setLevelFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Separator */}
        <div className="h-4 w-px bg-border" />

        {/* Category filter pills */}
        <div className="flex items-center gap-1 overflow-x-auto">
          {presentCategories.map((cat) => (
            <button
              key={cat}
              className={cn(
                'rounded px-1.5 py-0.5 text-[10px] font-medium transition-opacity',
                CATEGORY_COLORS[cat],
                categoryFilter && categoryFilter !== cat && 'opacity-40',
              )}
              onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Entry count */}
        <span className="text-xs text-muted-foreground">{filteredEntries.length} entries</span>

        {/* Clear button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onClear}
          title="Clear logs"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Log entries */}
      <div ref={scrollRef} className="relative flex-1 overflow-y-auto" onScroll={handleScroll}>
        {filteredEntries.map((entry) => (
          <LogEntryRow key={entry.id} entry={entry} />
        ))}
      </div>

      {/* Scroll to bottom button */}
      {!isAtBottom && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
          <Button
            variant="secondary"
            size="sm"
            className="h-7 gap-1 rounded-full px-3 text-xs shadow-md"
            onClick={scrollToBottom}
          >
            <ArrowDown className="h-3 w-3" />
            Scroll to bottom
          </Button>
        </div>
      )}
    </div>
  );
}
